import React, { useCallback, useMemo, useState } from 'react';
import { connect } from 'react-redux';
import { useAsyncFn } from 'react-use';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { bindActionCreators } from 'redux';

import fetchSalon from 'actions/fetchSalon.action';
import { ibeKpiBusinessPerformancesRead } from 'actions/kpi/beKpiBusinessPerformance.action';
import { abeSalon, ibeSalon } from 'actions/salon/beSalon.action';
import Button from 'components/form/Button.component';
import S from 'constants/string.const';
import { usePrepareFetcher } from 'hooks/usePrepare.hook';
import { fromPropsToMatchedSalonId } from 'utils/converters/props/bl.util';
import sleep from 'utils/functions/sleep.util';
import logger from 'utils/logger.util';

const L = logger('ActionsPage');

const SOURCE = Object.freeze({
  inert: 'inert',
  active: 'active',
  old: 'old',
  direct: 'direct',
  cache: 'cache',
  cancellable: 'cancellable',
  cancelled: 'cancelled',
});

const loggedQueryFn =
  (fn) =>
  async (...args) => {
    try {
      L.debug('slowQueryFn()', 'args:', args);
      const result = fn(...args);
      L.debug('slowQueryFn()', 'result', result);
      return result;
    } catch (error) {
      L.warn('slowQueryFn()', 'error:', error);
    }
  };

const ActionsPage = (props) => {
  const { ibeSalon: ibeSalonFromProps, abeSalon: abeSalonFromProps, fetchSalon: fetchSalonFromProps } = props;

  const [result, setResult] = useState(void 1);
  const [source, setSource] = useState(void 1);

  const handleClear = useCallback(() => {
    setSource(null);
    setResult('');
  }, []);

  const qc = useQueryClient();

  const salonId = fromPropsToMatchedSalonId(props);

  const prepSalon = usePrepareFetcher(ibeSalon);
  const resultSalon = useQuery({
    ...prepSalon,
    enabled: source === SOURCE.cancellable,
    queryFn: loggedQueryFn(prepSalon.queryFn),
  });

  const prepKpiRead = usePrepareFetcher(ibeKpiBusinessPerformancesRead);
  const resultKpiRead = useQuery({
    queryKey: prepKpiRead.queryKey,
    queryFn: prepKpiRead.queryFn,
    enabled: prepKpiRead.enabled,
    staleTime: prepKpiRead.staleTime,
  });

  const [, handleInertFetch] = useAsyncFn(async () => {
    const result = await ibeSalonFromProps();
    L.debug('handleInertFetch()', result);
    setResult(result);
    setSource(SOURCE.inert);
    return result;
  }, [ibeSalonFromProps]);

  const [, handleActiveFetch] = useAsyncFn(async () => {
    const result = await abeSalonFromProps();
    L.debug('handleActiveFetch()', result);
    setResult(result);
    setSource(SOURCE.active);
    return result;
  }, [abeSalonFromProps]);

  const [, handleOldFetch] = useAsyncFn(async () => {
    const result = await fetchSalonFromProps(salonId);
    L.debug('handleOldFetch()', result);
    setResult(result);
    setSource(SOURCE.old);
    return result;
  }, [fetchSalonFromProps, salonId]);

  const handleDirectFetchWithOptions = useCallback(() => {
    prepKpiRead.init({ bgn: '2024-12-01T00:00:00.000+01:00', end: '2025-01-01T00:00:00.000+01:00' });
    setSource(SOURCE.cache);
  }, [prepKpiRead]);

  const handleRefetch = useCallback(() => {
    L.unignore('handleRefetch()', 'refetch:', resultKpiRead.refetch());
    setSource(SOURCE.cache);
  }, [resultKpiRead]);

  const handleCancellableFetch = useCallback(() => {
    const isSalonOk = resultSalon.data?.is?.ok;
    if (!isSalonOk) {
      qc.removeQueries({ queryKey: prepSalon.queryKey });
      L.unignore('handleCancellableFetch()', 'refetch:', resultSalon.refetch());
    }
    setSource(SOURCE.cancellable);
  }, [prepSalon.queryKey, qc, resultSalon]);

  const handleCancel = useCallback(() => {
    try {
      L.unignore('handleCancel()', 'cancel:', qc.cancelQueries({ queryKey: prepSalon.queryKey }));
    } catch (e) {
      L.warn('handleCancel()', e);
    }
    setSource(SOURCE.cancelled);
  }, [prepSalon.queryKey, qc]);

  const handleCancelledFetch = useCallback(
    async (ev) => {
      L.debug('handleCancelledFetch()', 'in');
      ev.preventDefault();
      try {
        await sleep(100);
        // noinspection JSCheckFunctionSignatures
        const options = { exact: true, silent: true, queryKey: prepSalon.queryKey };
        await qc.cancelQueries(options, options);
      } catch (e) {
        L.warn('handleCancelledFetch()', e);
        setSource(SOURCE.cancellable);
      }
      setSource(SOURCE.cancelled);
      L.debug('handleCancelledFetch()', 'out');
    },
    [prepSalon.queryKey, qc],
  );

  const displayValue = useMemo(
    () =>
      !source
        ? ''
        : JSON.stringify(
            (() => {
              if (SOURCE.cache === source) return resultKpiRead.data;
              if (SOURCE.cancellable === source || SOURCE.cancelled === source) return resultSalon.data;
              return result;
            })(),
            null,
            2,
          ),
    [result, resultKpiRead.data, resultSalon.data, source],
  );

  if (resultKpiRead.error) L.warn('()', resultKpiRead.error);
  if (resultSalon.error) L.warn('()', resultSalon.error);

  L.debug('()', { prepKpiRead, resultKpiRead, prepSalon, resultSalon });

  return (
    <div data-bem="ActionsPage" style={{ position: 'absolute', inset: 0 }}>
      <div style={{ margin: '8px' }}>
        <Button onClick={handleClear}>Clear</Button>
        <span>Source: {source || '(none)'}</span>
      </div>

      <div style={{ display: 'inline-table', margin: '8px' }}>
        <Button onClick={handleInertFetch}>Inert fetch</Button>
        <Button onClick={handleActiveFetch}>Active fetch</Button>
        <Button onClick={handleOldFetch}>Old fetch</Button>
      </div>

      <div style={{ display: 'inline-table', margin: '8px' }}>
        <Button onClick={handleDirectFetchWithOptions}>Fetch with React Query Cache</Button>
        <Button onClick={handleRefetch}>Re-fetch with React Query</Button>
        <code>status</code>:<code>{resultKpiRead.status}</code>
        {S.nbsp}
        <code>fetchStatus</code>:<code>{resultKpiRead.fetchStatus}</code>
        {S.space}
        <div>
          <code>queryKey</code>:<code>{JSON.stringify(prepKpiRead.queryKey)}</code>
        </div>
      </div>

      <div style={{ display: 'inline-table', margin: '8px' }}>
        <Button onClick={handleCancellableFetch}>Fetch with Cancel</Button>
        <Button onClick={handleCancel}>Cancel It</Button>
        <Button onClick={handleCancelledFetch}>Fetch and Cancel</Button>
        <code>status</code>:<code>{resultSalon.status}</code>
        {S.nbsp}
        <code>fetchStatus</code>:<code>{resultSalon.fetchStatus}</code>
      </div>

      <textarea style={{ width: '99%', minHeight: '75%' }} value={displayValue} disabled></textarea>
    </div>
  );
};

const mapDispatchToProps = (dispatch) =>
  bindActionCreators(
    {
      ibeSalon,
      abeSalon,
      fetchSalon,
    },
    dispatch,
  );

const ActionsPageHoc = connect(null, mapDispatchToProps)(ActionsPage);

// noinspection JSUnusedGlobalSymbols
export default ActionsPageHoc;
